import React from 'react'
import { Card, Slider, Switch, Space, Typography, Divider, Button } from 'antd'
import { BlockOutlined, SettingOutlined } from '@ant-design/icons'
import { useDesignStore } from '../../store/designStore'

const { Title, Text } = Typography

interface TerrainControlsPanelProps {
  visible: boolean
  onClose: () => void
}

const TerrainControlsPanel: React.FC<TerrainControlsPanelProps> = ({ visible, onClose }) => {
  const { canvasSettings, updateCanvasSettings, toggle3DTerrain, updateTerrainSettings } = useDesignStore()

  if (!visible) return null

  const handleHeightScaleChange = (value: number) => {
    updateTerrainSettings({ heightScale: value })
  }

  const handleSegmentsChange = (value: number) => {
    updateTerrainSettings({ segments: value })
  }

  const handleBuildingAreaSizeChange = (value: number) => {
    updateTerrainSettings({ buildingAreaSize: value / 100 })
  }

  const handleOpacityChange = (value: number) => {
    updateCanvasSettings({ backgroundImageOpacity: value / 100 })
  }

  const handleScaleChange = (value: number) => {
    updateCanvasSettings({ backgroundImageScale: value / 100 })
  }

  return (
    <Card
      title={
        <Space>
          <BlockOutlined />
          <span>3D Terrain Controls</span>
        </Space>
      }
      extra={
        <Button type="text" onClick={onClose}>
          ×
        </Button>
      }
      style={{
        position: 'absolute',
        top: 80,
        right: 20,
        width: 320,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
        zIndex: 1000,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
      }}
      bodyStyle={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto' }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* 3D Terrain Toggle */}
        <div>
          <Space>
            <Text strong>Enable 3D Terrain</Text>
            <Switch
              checked={canvasSettings.enable3DTerrain}
              onChange={toggle3DTerrain}
              size="small"
            />
          </Space>
          <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: 4 }}>
            Convert uploaded image to 3D buildable terrain
          </Text>
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Terrain Settings */}
        {canvasSettings.enable3DTerrain && (
          <>
            <div>
              <Text strong>Height Scale: {canvasSettings.terrainHeightScale}</Text>
              <Slider
                min={0.5}
                max={8}
                step={0.5}
                value={canvasSettings.terrainHeightScale}
                onChange={handleHeightScaleChange}
                tooltip={{ formatter: (value) => `${value}x` }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Controls terrain elevation intensity
              </Text>
            </div>

            <div>
              <Text strong>Detail Level: {canvasSettings.terrainSegments}</Text>
              <Slider
                min={32}
                max={128}
                step={16}
                value={canvasSettings.terrainSegments}
                onChange={handleSegmentsChange}
                tooltip={{ formatter: (value) => `${value} segments` }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Higher values = more detail (slower performance)
              </Text>
            </div>

            <div>
              <Text strong>Building Area: {Math.round((canvasSettings.terrainBuildingAreaSize || 0.25) * 100)}%</Text>
              <Slider
                min={10}
                max={50}
                step={5}
                value={Math.round((canvasSettings.terrainBuildingAreaSize || 0.25) * 100)}
                onChange={handleBuildingAreaSizeChange}
                tooltip={{ formatter: (value) => `${value}% flat area` }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Size of flat building area in center
              </Text>
            </div>

            <Divider style={{ margin: '12px 0' }} />
          </>
        )}

        {/* Image Settings */}
        {canvasSettings.backgroundImage && (
          <>
            <div>
              <Text strong>Image Opacity: {Math.round((canvasSettings.backgroundImageOpacity || 0.7) * 100)}%</Text>
              <Slider
                min={10}
                max={100}
                step={5}
                value={Math.round((canvasSettings.backgroundImageOpacity || 0.7) * 100)}
                onChange={handleOpacityChange}
                tooltip={{ formatter: (value) => `${value}%` }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Adjust image visibility on terrain
              </Text>
            </div>

            <div>
              <Text strong>Image Scale: {Math.round((canvasSettings.backgroundImageScale || 1.0) * 100)}%</Text>
              <Slider
                min={50}
                max={200}
                step={10}
                value={Math.round((canvasSettings.backgroundImageScale || 1.0) * 100)}
                onChange={handleScaleChange}
                tooltip={{ formatter: (value) => `${value}%` }}
              />
              <Text type="secondary" style={{ fontSize: '12px' }}>
                Resize the background image
              </Text>
            </div>
          </>
        )}

        {/* Tips */}
        <div style={{ 
          background: '#f6ffed', 
          border: '1px solid #b7eb8f', 
          borderRadius: 6, 
          padding: 12,
          marginTop: 8
        }}>
          <Text style={{ fontSize: '12px', color: '#52c41a' }}>
            <strong>💡 Tips:</strong><br />
            • Lower height scale for flatter, more buildable terrain<br />
            • The system automatically flattens building areas<br />
            • Use 64 segments for good detail/performance balance
          </Text>
        </div>
      </Space>
    </Card>
  )
}

export default TerrainControlsPanel
